import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function About() {
  const values = [
    { icon: 'ri-shield-check-line', title: '信頼', desc: '誠実さと透明性を大切に、お客様との長期的な信頼関係を築きます。' },
    { icon: 'ri-lightbulb-line', title: '革新', desc: '最新技術と創造的な発想で、時代を超えた価値ある空間を提供します。' },
    { icon: 'ri-leaf-line', title: '持続可能性', desc: '環境に配慮した建築で、次世代へ美しい地球を引き継ぎます。' },
    { icon: 'ri-heart-line', title: '地域密着', desc: '地域の文化と人々の暮らしに寄り添い、共に成長します。' },
  ];

  const { ref: sectionRef, isVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: profileRef, isVisible: profileVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="about" className="py-16 md:py-28 bg-[#F8F6F1]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <div
            ref={sectionRef as React.RefObject<HTMLDivElement>}
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0)' : 'translateX(-48px)',
              transition: 'opacity 0.8s ease, transform 0.8s ease',
            }}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#B8960C]"></div>
              <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">About Us</span>
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-6 leading-tight"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              顧客と共に<br />価値を創造する
            </h2>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
              株式会社永恒は2019年の創業以来、「信頼と革新」を経営理念に掲げ、高品質な内装仕上工事を通じて、人々の暮らしを豊かにしてきました。
            </p>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-10">
              私たちのビジョンは、革新的な技術と地域密着のサービスで、持続可能な社会を実現することです。お客様一人ひとりのライフスタイルに寄り添い、理想の住まいを共に創り上げます。
            </p>

            {/* Values Grid */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {values.map((v, i) => (
                <div
                  key={i}
                  className="bg-white p-3 md:p-5 rounded-lg border border-gray-100 hover:border-[#B8960C]/40 hover:shadow-md transition-all"
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
                    transition: `opacity 0.6s ease ${0.3 + i * 0.12}s, transform 0.6s ease ${0.3 + i * 0.12}s`,
                  }}
                >
                  <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-[#B8960C]/10 rounded-md mb-2 md:mb-3">
                    <i className={`${v.icon} text-lg md:text-xl text-[#B8960C]`}></i>
                  </div>
                  <h4 className="font-bold text-[#1A1A1A] mb-1 text-sm md:text-base tracking-wider">{v.title}</h4>
                  <p className="text-gray-500 text-xs md:text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className="relative"
            style={{
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(48px)',
              transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s',
            }}
          >
            <div className="w-full h-[250px] md:h-[400px] rounded-sm overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/153ac9427d1f7f9b4d014e76b8fbc173.jpg"
                alt="株式会社永恒 オフィス"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="hidden lg:block absolute -top-4 -right-4 w-32 h-32 border-2 border-[#B8960C]/30 rounded-sm pointer-events-none"></div>
          </div>
        </div>

        {/* 会社概要 */}
        <div
          ref={profileRef as React.RefObject<HTMLDivElement>}
          className="mt-16 md:mt-24"
          style={{
            opacity: profileVisible ? 1 : 0,
            transform: profileVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s',
          }}
        >
          <div className="flex items-center gap-3 mb-8 md:mb-10">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Company Profile</span>
            <h3 className="text-xl md:text-2xl font-bold text-[#1A1A1A] ml-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              会社概要
            </h3>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            {[
              { label: '社名', value: '株式会社永恒' },
              { label: '住所', value: '〒272-0001 千葉県市川市二俣2-14-18' },
              { label: 'TEL / FAX', value: 'TEL: 047-722-1925　／　FAX: 047-722-1925' },
              { label: '代表取締役', value: '安喰弘彬 / 安喰慧美' },
              { label: '創業', value: '2019年7月' },
              { label: '資本金', value: '900万円' },
              { label: '従業員', value: '15名' },
              { label: '事業内容', value: '内装仕上工事業　建設許可：千葉県知事許可（般-5）第56257号' },
              { label: '取引銀行', value: '三菱UFJ銀行　／　東京ベイ信用金庫　／　GMOあおぞらネット銀行' },
            ].map((row, i) => (
              <div
                key={i}
                className={`flex flex-col sm:flex-row ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8F6F1]'} border-b border-gray-100 last:border-b-0`}
              >
                <div className="sm:w-48 px-4 md:px-6 py-3 md:py-4 flex items-center">
                  <span className="text-sm md:text-base font-semibold text-[#B8960C] tracking-wider whitespace-nowrap">{row.label}</span>
                </div>
                <div className="flex-1 px-4 md:px-6 py-3 md:py-4 border-t sm:border-t-0 sm:border-l border-gray-100">
                  <span className="text-sm md:text-base text-[#1A1A1A] leading-relaxed">{row.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}